import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { SelectButton } from 'primereact/selectbutton';
import { Button } from 'primereact/button';
import MLMUserReportsModal from '../mlm/MLMUserReportsModal';
import MLMUserLeadsModal from '../mlm/MLMUserLeadsModal';

/**
 * Org Chart Position Panel Component
 * Displays selected position's aggregated MLM performance data
 * Uses PrimeReact + PrimeFlex only, follows Diamond template patterns
 */
const OrgChartPositionPanel = ({ positionData, loading, error, onFilterChange, currentFilter, onViewMLMTree }) => {
    // Reports modal state
    const [showReportsModal, setShowReportsModal] = useState(false);
    const [reportType, setReportType] = useState('total'); // 'my', 'network', or 'total'
    // Leads modal state
    const [showLeadsModal, setShowLeadsModal] = useState(false);
    const [leadType, setLeadType] = useState('total'); // 'my', 'network', or 'total'

    // Filter options
    const filterOptions = [
        { label: 'Whole Branch', value: 'whole_branch', icon: 'pi pi-sitemap' },
        { label: 'This Position Only', value: 'this_position_only', icon: 'pi pi-user' }
    ];

    // Template for filter buttons
    const filterTemplate = (option) => {
        return (
            <div className="flex align-items-center gap-2">
                <i className={option.icon}></i>
                <span>{option.label}</span>
            </div>
        );
    };

    // Loading State
    if (loading) {
        return (
            <div className="flex align-items-center justify-content-center" style={{ minHeight: '300px' }}>
                <div className="text-center">
                    <ProgressSpinner />
                    <p className="mt-3 text-600">Loading position performance data...</p>
                </div>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <Card className="mb-3">
                <Message severity="error" text={error} className="w-full" />
            </Card>
        );
    }

    // Empty State
    if (!positionData) {
        return (
            <Card className="mb-3">
                <Message
                    severity="info"
                    text="Click on a position in the org chart to view MLM performance data"
                    className="w-full"
                />
            </Card>
        );
    }

    const { position, aggregate, filter, positions_count, mlm_users_count, total_users_count } = positionData;

    return (
        <div>
            {/* Position Header - Responsive */}
            <Card className="mb-2 md:mb-3 p-2 md:p-3">
                <div className="flex flex-column sm:flex-row align-items-start sm:align-items-center justify-content-between gap-2">
                    <div className="flex align-items-center gap-2">
                        <Avatar
                            icon="pi pi-briefcase"
                            size="normal"
                            shape="circle"
                            style={{ backgroundColor: '#4F46E5', color: '#ffffff' }}
                        />
                        <div>
                            <span className="font-bold text-900 text-sm md:text-base">{position.name}</span>
                            <span className="text-xs text-500 ml-2">Level {position.level}</span>
                        </div>
                    </div>
                    {position.user ? (
                        <div className="flex align-items-center gap-2">
                            <Avatar
                                label={position.user.name.charAt(0).toUpperCase()}
                                size="normal"
                                shape="circle"
                                style={{ backgroundColor: '#10B981', color: '#ffffff' }}
                            />
                            <div className="text-right">
                                <div className="text-sm font-medium text-900">{position.user.name}</div>
                                <div className="text-xs text-500 hidden sm:block">{position.user.email}</div>
                            </div>
                        </div>
                    ) : (
                        <Tag value="Unassigned" severity="warning" />
                    )}
                </div>
            </Card>

            {/* Filter Selector */}
            <Card className="mb-2 md:mb-3 p-2 md:p-3">
                <h5 className="m-0 mb-2 md:mb-3 text-sm md:text-md font-semibold text-900">View Mode</h5>
                <SelectButton
                    value={currentFilter}
                    onChange={(e) => onFilterChange && onFilterChange(e.value)}
                    options={filterOptions}
                    optionLabel="label"
                    optionValue="value"
                    itemTemplate={filterTemplate}
                    className="w-full flex-wrap"
                />
                <div className="mt-2 md:mt-3 p-2 md:p-3 surface-100 border-round">
                    <div className="flex align-items-center gap-2 mb-2">
                        <i className="pi pi-info-circle text-600"></i>
                        <span className="text-xs md:text-sm font-medium text-600">
                            {filter === 'whole_branch' ? 'Showing aggregated data for entire branch' : 'Showing data for this position only'}
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2 md:gap-3 text-xs text-600">
                        <span><i className="pi pi-users mr-1"></i>{positions_count} position{positions_count !== 1 ? 's' : ''}</span>
                        <span><i className="pi pi-user mr-1"></i>{total_users_count} user{total_users_count !== 1 ? 's' : ''}</span>
                        <span><i className="pi pi-chart-bar mr-1"></i>{mlm_users_count} in MLM</span>
                    </div>
                </div>
            </Card>

            {/* MLM Deep Dive Button - Only show if user is in MLM */}
            {position.user && position.user.is_in_mlm && (
                <Card className="mb-2 md:mb-3 p-2 md:p-3">
                    <div className="flex flex-column sm:flex-row align-items-start sm:align-items-center justify-content-between gap-2 sm:gap-3">
                        <div className="flex-1">
                            <h5 className="m-0 mb-1 sm:mb-2 text-sm md:text-md font-semibold text-900">Explore MLM Network</h5>
                            <p className="m-0 text-xs md:text-sm text-600">View complete MLM tree and drill down into network details</p>
                        </div>
                        <Button
                            label="View MLM Tree"
                            icon="pi pi-share-alt"
                            className="p-button-primary p-button-sm md:p-button-md w-full sm:w-auto"
                            onClick={() => onViewMLMTree && onViewMLMTree(positionData)}
                            tooltip="Open full MLM network in modal"
                            tooltipOptions={{ position: 'left' }}
                        />
                    </div>
                </Card>
            )}

            {/* Aggregate Performance Stats */}
            <h4 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-900">📊 Aggregated MLM Performance</h4>
            <div className="grid mb-2 md:mb-3">
                <div className="col-6">
                    <Card className="h-full p-2 md:p-3">
                        <div className="flex align-items-center gap-2 md:gap-3">
                            <i className="pi pi-users text-2xl md:text-4xl" style={{ color: '#4F46E5' }}></i>
                            <div className="flex-1">
                                <div className="text-xl md:text-4xl font-bold text-900">{aggregate.total_network_size}</div>
                                <div className="text-xs text-500 font-medium uppercase">Total Network</div>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-6">
                    <Card
                        className="h-full p-2 md:p-3 cursor-pointer hover:surface-100 transition-colors transition-duration-200"
                        onClick={() => setShowReportsModal(true)}
                    >
                        <div className="flex align-items-center gap-2 md:gap-3">
                            <i className="pi pi-file text-2xl md:text-4xl" style={{ color: '#10B981' }}></i>
                            <div className="flex-1">
                                <div className="flex align-items-center gap-1 md:gap-2">
                                    <span className="text-xl md:text-4xl font-bold text-900">{aggregate.total_reports}</span>
                                    <i className="pi pi-external-link text-xs md:text-sm text-500"></i>
                                </div>
                                <div className="text-xs text-500 font-medium uppercase">Total Reports</div>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-6">
                    <Card className="h-full p-2 md:p-3">
                        <div className="flex align-items-center gap-2 md:gap-3">
                            <i className="pi pi-user-plus text-2xl md:text-4xl" style={{ color: '#F59E0B' }}></i>
                            <div className="flex-1">
                                <div className="text-xl md:text-4xl font-bold text-900">{aggregate.total_direct_referrals}</div>
                                <div className="text-xs text-500 font-medium uppercase">Direct Referrals</div>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-6">
                    <Card className="h-full p-2 md:p-3">
                        <div className="flex align-items-center gap-2 md:gap-3">
                            <i className="pi pi-sitemap text-2xl md:text-4xl" style={{ color: '#8B5CF6' }}></i>
                            <div className="flex-1">
                                <div className="text-xl md:text-4xl font-bold text-900">{aggregate.max_depth}</div>
                                <div className="text-xs text-500 font-medium uppercase">Max Depth</div>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-6">
                    <Card
                        className="h-full p-2 md:p-3 cursor-pointer hover:surface-100 transition-colors transition-duration-200"
                        onClick={() => setShowLeadsModal(true)}
                    >
                        <div className="flex align-items-center gap-2 md:gap-3">
                            <i className="pi pi-id-card text-2xl md:text-4xl" style={{ color: '#EC4899' }}></i>
                            <div className="flex-1">
                                <div className="flex align-items-center gap-1 md:gap-2">
                                    <span className="text-xl md:text-4xl font-bold text-900">{aggregate.total_leads || 0}</span>
                                    <i className="pi pi-external-link text-xs md:text-sm text-500"></i>
                                </div>
                                <div className="text-xs text-500 font-medium uppercase">Total Leads</div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Reports Breakdown Card */}
            <Card className="mb-2 md:mb-3 p-2 md:p-3">
                <h4 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-900">📈 Reports Breakdown</h4>

                <div
                    className="flex justify-content-between align-items-center py-2 p-2 cursor-pointer hover:surface-100 border-round transition-colors transition-duration-200"
                    onClick={() => {
                        setReportType('my');
                        setShowReportsModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view my reports"
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-user text-600"></i>
                        <span className="text-600 font-medium">My Reports</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <span className="text-xl font-bold text-900">{aggregate.my_reports}</span>
                        <i className="pi pi-external-link text-sm text-500"></i>
                    </div>
                </div>

                <div
                    className="flex justify-content-between align-items-center py-2 p-2 cursor-pointer hover:surface-100 border-round transition-colors transition-duration-200"
                    onClick={() => {
                        setReportType('network');
                        setShowReportsModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view network reports"
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-users text-600"></i>
                        <span className="text-600 font-medium">Network Reports</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <span className="text-xl font-bold text-900">{aggregate.network_reports}</span>
                        <i className="pi pi-external-link text-sm text-500"></i>
                    </div>
                </div>

                <Divider />

                <div
                    className="flex justify-content-between align-items-center pt-2 p-2 cursor-pointer hover:surface-100 border-round transition-colors transition-duration-200"
                    onClick={() => {
                        setReportType('total');
                        setShowReportsModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view all reports"
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-chart-bar text-primary"></i>
                        <span className="text-900 font-bold">Total Reports</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <span className="text-2xl font-bold text-primary">{aggregate.total_reports}</span>
                        <i className="pi pi-external-link text-sm text-500"></i>
                    </div>
                </div>
            </Card>

            {/* Leads Breakdown Card */}
            <Card className="mb-2 md:mb-3 p-2 md:p-3">
                <h4 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-900">👥 Leads Breakdown</h4>

                <div
                    className="flex justify-content-between align-items-center py-2 p-2 cursor-pointer hover:surface-100 border-round transition-colors transition-duration-200"
                    onClick={() => {
                        setLeadType('my');
                        setShowLeadsModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view my leads"
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-user text-600"></i>
                        <span className="text-600 font-medium">My Leads</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <span className="text-xl font-bold text-900">{aggregate.my_leads || 0}</span>
                        <i className="pi pi-external-link text-sm text-500"></i>
                    </div>
                </div>

                <div
                    className="flex justify-content-between align-items-center py-2 p-2 cursor-pointer hover:surface-100 border-round transition-colors transition-duration-200"
                    onClick={() => {
                        setLeadType('network');
                        setShowLeadsModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view network leads"
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-users text-600"></i>
                        <span className="text-600 font-medium">Network Leads</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <span className="text-xl font-bold text-900">{aggregate.network_leads || 0}</span>
                        <i className="pi pi-external-link text-sm text-500"></i>
                    </div>
                </div>

                <Divider />

                <div
                    className="flex justify-content-between align-items-center pt-2 p-2 cursor-pointer hover:surface-100 border-round transition-colors transition-duration-200"
                    onClick={() => {
                        setLeadType('total');
                        setShowLeadsModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view all leads"
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-id-card" style={{ color: '#EC4899' }}></i>
                        <span className="text-900 font-bold">Total Leads</span>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <span className="text-2xl font-bold" style={{ color: '#EC4899' }}>{aggregate.total_leads || 0}</span>
                        <i className="pi pi-external-link text-sm text-500"></i>
                    </div>
                </div>
            </Card>

            {/* Processing Info Card */}
            <Card className="mb-2 md:mb-3 p-2 md:p-3">
                <h4 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-900">ℹ️ Processing Summary</h4>

                <div className="flex justify-content-between align-items-center py-1 md:py-2">
                    <span className="text-xs md:text-sm text-600 font-medium">Users Processed</span>
                    <Tag value={aggregate.users_processed} severity="success" />
                </div>

                {aggregate.users_failed && aggregate.users_failed.length > 0 && (
                    <>
                        <Divider />
                        <div className="py-1 md:py-2">
                            <div className="flex align-items-center gap-2 mb-2">
                                <i className="pi pi-exclamation-triangle text-orange-500 text-sm md:text-base"></i>
                                <span className="text-xs md:text-sm text-600 font-medium">Users Not in MLM</span>
                                <Tag value={aggregate.users_failed.length} severity="warning" />
                            </div>
                            <div className="surface-100 border-round p-2">
                                {aggregate.users_failed.map((userName, index) => (
                                    <div key={index} className="text-xs text-600 py-1">
                                        • {userName}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </Card>

            {/* Position Reports Modal */}
            <MLMUserReportsModal
                visible={showReportsModal}
                onHide={() => setShowReportsModal(false)}
                positionId={position.id}
                userName={position.name}
                reportType={reportType}
                accessType="orgposition"
                filterType={currentFilter}
            />

            {/* Position Leads Modal */}
            <MLMUserLeadsModal
                visible={showLeadsModal}
                onHide={() => setShowLeadsModal(false)}
                positionId={position.id}
                userName={position.name}
                leadType={leadType}
                accessType="orgposition"
                filterType={currentFilter}
            />
        </div>
    );
};

export default OrgChartPositionPanel;
